import { Section } from '@vnetwork/ui/layout';
import {
  Card,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Disclosure,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import type { JobFormData, OutputKey, HlsProtectionGroup, OutputDistributionConfig } from './use-job-create-form';
import styles from './JobCreateDistributionSection.module.css';

const TARGET_OPTIONS = [
  { value: 'cdn', label: 'CDN Endpoint' },
  { value: 'ftp', label: 'FTP' },
  { value: 'sftp', label: 'SFTP' },
  { value: 'azure', label: 'Microsoft Azure' },
  { value: 's3', label: 'AWS S3 / OSS' },
];

const OUTPUT_LABELS: Record<OutputKey, string> = {
  hls: 'HLS',
  dash: 'MPEG-DASH',
  mss: 'MS-SSTR',
  mp4: 'MP4',
};

interface StorageSelectProps {
  target: string;
  storages: Record<string, unknown>[];
  value: string;
  onChange: (v: string) => void;
  id?: string;
  'aria-describedby'?: string;
}

function StorageSelect({ target, storages, value, onChange, ...ariaProps }: StorageSelectProps) {
  if (storages.length === 0) {
    return <Alert tone="warning">No {target.toUpperCase()} storages configured.</Alert>;
  }
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger size="sm" {...ariaProps}><SelectValue placeholder="Select storage…" /></SelectTrigger>
      <SelectContent>
        {storages.map((s) => {
          const val = String(s?.id ?? s?.name ?? '');
          return (
            <SelectItem key={val} value={val}>
              {String(s?.name ?? val)}
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

interface DistributionRowProps {
  label: string;
  target: string;
  storage: string;
  path: string;
  storages: Record<string, unknown>[];
  onTargetChange: (v: string) => void;
  onStorageChange: (v: string) => void;
  onPathChange: (v: string) => void;
  pathPlaceholder?: string;
}

function DistributionRow({
  label,
  target,
  storage,
  path,
  storages,
  onTargetChange,
  onStorageChange,
  onPathChange,
  pathPlaceholder = '/output/path/',
}: DistributionRowProps) {
  return (
    <div className={styles.distributionRow}>
      {label && <p className={styles.rowLabel}>{label}</p>}
      <div className={styles.distributionFields}>
        <Field label="Target">
          {(p) => (
            <Select value={target} onValueChange={onTargetChange}>
              <SelectTrigger size="sm" {...p}><SelectValue /></SelectTrigger>
              <SelectContent>
                {TARGET_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
        <Field label="Storage">
          {(p) => (
            <StorageSelect
              target={target}
              storages={storages}
              value={storage}
              onChange={onStorageChange}
              {...p}
            />
          )}
        </Field>
        <Field label="Output Path" orientation="vertical">
          {(p) => (
            <Input
              {...p}
              className={styles.pathInput}
              placeholder={pathPlaceholder}
              value={path}
              onChange={(e) => onPathChange(e.target.value)}
            />
          )}
        </Field>
      </div>
    </div>
  );
}

interface Props {
  form: JobFormData;
  isOutputSelected: (k: OutputKey) => boolean;
  getStoragesByTarget: (target: string) => Record<string, unknown>[];
  updateDistributionDraft: (k: OutputKey, field: keyof OutputDistributionConfig, value: string) => void;
  updateDistributionGroupDraft: (k: OutputKey, group: HlsProtectionGroup, field: keyof OutputDistributionConfig, value: string) => void;
}

export function JobCreateDistributionSection({
  form,
  isOutputSelected,
  getStoragesByTarget,
  updateDistributionDraft,
  updateDistributionGroupDraft,
}: Props) {
  const selectedOutputs = (Object.keys(OUTPUT_LABELS) as OutputKey[]).filter(isOutputSelected);

  return (
    <Section title="Distribution" style={{ marginBottom: 0 }}>
      <Card flat className={styles.cardInner}>
        {selectedOutputs.length === 0 ? (
          <p className={styles.empty}>Select at least one output format above to configure distribution.</p>
        ) : (
          <div className={styles.accordionList}>
            {selectedOutputs.map((outputKey) => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const cfg = form.outputOptions[outputKey] as any;

              return (
                <Disclosure
                  key={outputKey}
                  defaultOpen
                  summary={
                    <span className={styles.disclosureTitle}>
                      {OUTPUT_LABELS[outputKey]} Distribution
                    </span>
                  }
                >
                  {outputKey === 'hls' ? (
                    // HLS: one row per protection group that's enabled
                    <div className={styles.hlsGroups}>
                      {(
                        [
                          { key: 'none' as HlsProtectionGroup, label: 'HLS: None', enabled: Boolean(cfg.noneEnabled), defaultEncrypt: '3' },
                          { key: 'aes' as HlsProtectionGroup, label: 'HLS: AES', enabled: Boolean(cfg.aesEnabled), defaultEncrypt: '2' },
                          { key: 'verimatrix' as HlsProtectionGroup, label: 'HLS: Verimatrix DRM', enabled: Boolean(cfg.verimatrixEnabled), defaultEncrypt: '1' },
                        ]
                      ).map((group) => {
                        if (!group.enabled) return null;
                        const draft: OutputDistributionConfig = cfg.draftDistributionGroups?.[group.key] ?? {
                          target: 'cdn', storage: '', path: '', encryptType: group.defaultEncrypt,
                        };
                        const storages = getStoragesByTarget(draft.target);
                        return (
                          <DistributionRow
                            key={group.key}
                            label={group.label}
                            target={draft.target}
                            storage={draft.storage}
                            path={draft.path}
                            storages={storages}
                            onTargetChange={(v) => updateDistributionGroupDraft(outputKey, group.key, 'target', v)}
                            onStorageChange={(v) => updateDistributionGroupDraft(outputKey, group.key, 'storage', v)}
                            onPathChange={(v) => updateDistributionGroupDraft(outputKey, group.key, 'path', v)}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    // Non-HLS: single distribution config
                    (() => {
                      const draft: OutputDistributionConfig = cfg.draftDistribution ?? { target: 'cdn', storage: '', path: '', encryptType: '3' };
                      const storages = getStoragesByTarget(draft.target);
                      return (
                        <>
                          <DistributionRow
                            label=""
                            target={draft.target}
                            storage={draft.storage}
                            path={draft.path}
                            storages={storages}
                            onTargetChange={(v) => updateDistributionDraft(outputKey, 'target', v)}
                            onStorageChange={(v) => updateDistributionDraft(outputKey, 'storage', v)}
                            onPathChange={(v) => updateDistributionDraft(outputKey, 'path', v)}
                          />
                          <p className={styles.encryptHint}>No encryption applied</p>
                        </>
                      );
                    })()
                  )}
                </Disclosure>
              );
            })}
          </div>
        )}
      </Card>
    </Section>
  );
}
